import React from 'react';
import { css } from '@emotion/react';
import tw from 'twin.macro';
import * as yup from 'yup';

import BillingContact from '$/components/ui/Input/BillingContacts/BillingContact';
import Input from '../Basics/Input';
import Text from '../../Text';

const newBillingContactSchema = yup.string().required().trim().email();

interface BillingContactsInputProps {
  onChange?: (value: string[]) => void;
  isReadOnly?: boolean;
  value?: string[];
}

export const BillingContactsInput: React.FC<BillingContactsInputProps> = ({
  value = [],
  onChange,
  isReadOnly,
}) => {
  const [newBillingContact, updateNewBillingContact] = React.useState<string>('');
  const [error, updateError] = React.useState<string>('');

  const onAdd = React.useCallback(() => {
    const isValidEmail = newBillingContactSchema.isValidSync(newBillingContact);
    if (!isValidEmail) {
      updateError('Please provide a valid email address.');
      return;
    }
    const isAlready = value.includes(newBillingContact);
    if (isAlready) {
      updateError('Email address already exits.');
      return;
    }
    onChange?.([...value, newBillingContact]);
    updateNewBillingContact('');
  }, [onChange, value, newBillingContact]);

  const onRemove = React.useCallback(
    (email: string) => {
      onChange?.(value.filter((item: string) => item !== email));
    },
    [onChange, value]
  );

  const onInputChange = React.useCallback((value: string) => {
    updateError('');
    updateNewBillingContact(value);
  }, []);

  return (
    <>
      <div
        className='flex flex-col item-start w-full'
        css={css`
          max-width: 25rem;
        `}
      >
        {value &&
          value.map((email: string) => (
            <BillingContact
              key={email}
              email={email}
              onRemove={onRemove}
              isReadOnly={Boolean(isReadOnly)}
            />
          ))}

        {!isReadOnly && (
          <>
            <Input
              value={newBillingContact}
              onChange={onInputChange}
              type='email'
              placeholder='Email'
              action={
                <div
                  css={[
                    tw`text-blue-3 px-3 py-2 cursor-pointer border border-color-input-box rounded-input`,
                    css`
                      border-left: none;
                      border-top-left-radius: 0;
                      border-bottom-left-radius: 0;
                      line-height: 1.6rem;
                      margin-left: -1px;
                    `,
                  ]}
                  onClick={onAdd}
                >
                  Add
                </div>
              }
            />
            {error && (
              <Text className='inline-block mt-4 ml-4 text-md text-color-error'>{error}</Text>
            )}
          </>
        )}
      </div>
    </>
  );
};
